import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  /** Extra classes for the message paragraph, e.g. 'break-words' for long unbreakable copy. */
  messageClassName?: string;
  confirmText?: string;
  cancelText?: string;
  confirmPosition?: 'left' | 'right';
  confirmVariant?: 'destructive' | 'primary';
  /** Disables the buttons and dismissal while an action triggered from the modal is running. */
  isBusy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Close button and backdrop click; defaults to onCancel. */
  onDismiss?: () => void;
}

export const ConfirmModal = ({
  isOpen,
  title = 'Unsaved Changes',
  message,
  messageClassName = '',
  confirmText = 'Leave',
  cancelText = 'Stay',
  confirmPosition = 'left',
  confirmVariant = 'destructive',
  isBusy = false,
  onConfirm,
  onCancel,
  onDismiss,
}: ConfirmModalProps) => {
  if (!isOpen) return null;

  const handleDismiss = () => {
    if (!isBusy) {
      (onDismiss ?? onCancel)();
    }
  };

  const confirmButtonClass =
    confirmVariant === 'primary'
      ? 'flex-1 px-4 py-3 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed'
      : 'flex-1 px-4 py-3 border-2 border-destructive/50 text-destructive rounded-lg hover:bg-destructive/10 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed';
  const cancelButtonClass =
    confirmVariant === 'primary'
      ? 'flex-1 px-4 py-3 border-2 border-border text-subtle-foreground rounded-lg hover:bg-accent transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed'
      : 'flex-1 px-4 py-3 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed';

  const confirmButton = (
    <button
      type="button"
      onClick={onConfirm}
      disabled={isBusy}
      className={confirmButtonClass}
    >
      {confirmText}
    </button>
  );
  const cancelButton = (
    <button
      type="button"
      onClick={onCancel}
      disabled={isBusy}
      className={cancelButtonClass}
    >
      {cancelText}
    </button>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleDismiss}
    >
      <div
        className="relative bg-card rounded-xl shadow-2xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-border">
          <div className="p-2 bg-orange-100 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
          </div>
          <h3 className="text-lg font-bold text-foreground">{title}</h3>
          <button
            type="button"
            onClick={handleDismiss}
            disabled={isBusy}
            aria-label="Close"
            className="ml-auto p-2 hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          <p className={`text-foreground ${messageClassName}`}>{message}</p>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-border">
          {confirmPosition === 'left' ? (
            <>
              {confirmButton}
              {cancelButton}
            </>
          ) : (
            <>
              {cancelButton}
              {confirmButton}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
